<template>
  <div :style="searchWrapperStyle">
    <form autocomplete="off" novalidate @submit.prevent>
      <LeadInput
        ref="leadInputRef"
        v-model="internalValue"
        :placeholder="placeholder"
        :leading-icon="IconSearch"
        data-testid="header-navbar-search-input"
        @keyup="handleKeyUp"
        @focus="handleFocus" />
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import translator from '@/locale/translator.js';

import { IconSearch } from '@tabler/icons-vue';
import type { SortOption, SortState } from '@/components/generic/SortButton.vue';
import LeadInput from '@/components/lead-design/LeadInput.vue';

export interface Props {
  searchBarWidth?: number;
  placeholder?: string;
  modelValue: string;
  sortOptions?: SortOption[];
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const sort = defineModel<SortState>('sort', { required: false });

const props = withDefaults(defineProps<Props>(), {
  searchBarWidth: 300,
  placeholder: translator.get('search.generic_placeholder'),
});

const emit = defineEmits<{
  (event: 'update:modelValue', value: string | null): void;
  (event: 'focus'): void;
}>();

// LeadInput exposes `focus()` and its underlying `inputRef` via defineExpose.
const leadInputRef = ref<{ focus: () => void; inputRef: HTMLInputElement | null } | null>(null);

const internalValue = computed({
  get() {
    return props.modelValue ?? '';
  },
  set(value) {
    emit('update:modelValue', value as string);
  },
});

const searchWrapperStyle = computed(() => ({
  width: `${props.searchBarWidth}px`,
}));

onMounted(() => {
  document.addEventListener('keydown', handleKeyDown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown);
});

function handleFocus() {
  emit('focus');
}

function handleKeyDown(event: KeyboardEvent) {
  if (event.key === '/') {
    leadInputRef.value?.focus();
    event.preventDefault();
  }
}

function handleKeyUp(event: KeyboardEvent) {
  if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
    event.stopPropagation();
  }
}
</script>
